import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Tabs,
  DataTable,
  type Column,
  Pill,
  Button,
  Avatar,
  EmptyState,
  Drawer,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  DrawerTitle,
  AlertDialog,
  Textarea,
} from "@/design/components";
import { MapPin, Camera } from "lucide-react";
import { formatPct, formatRelativeShort } from "@/design/format";
import { AssuranceBadge, HeimdallBadge, AnalystReviewingIndicator } from "@/design/components";
import { useSeed, usePeopleIndex, useOrgIndex, useMoney, useAssurance, useAiReviewFor, useAnalystName, useMutate, useProjects } from "@/domain/hooks";
import { useDisbursementActions } from "@/domain/useMutations";
import {
  useProjectCreationRequests,
  useRejectProjectCreationRequest,
  type ProjectCreationRequestRow,
} from "@/domain/useProjectCreation";
import { CreateProjectModal } from "@/components/CreateProjectModal";
import { SpendAskDrawer } from "@/screens/approval/SpendAskDrawer";
import { VerificationSummaryCard } from "@/screens/approval/VerificationSummaryCard";
import { OnboardingDetailDrawer, type GateRow } from "@/screens/stratus/OnboardingDetailDrawer";
import type { SpendRequest, Disbursement } from "@/domain/types";
import { api, demoModeActive, onboardingApi, type GateOrg } from "@/domain/api";
import { isAboveStratusThreshold } from "@/domain/spendThreshold";

// A row for the onboarding approval gate — either a seed org (demo) or a row from
// GET /onboarding/gates (API). id/name/tier drive the queue; the API summary fields
// (legalName/kycState/bankState) feed the detail drawer when the org isn't in the
// seed store (off-demo). GateRow is defined in OnboardingDetailDrawer.
function gateRowFromApi(g: GateOrg): GateRow {
  return {
    id: String(g.id),
    name: g.name || g.legalname || `Organisation ${g.id}`,
    tier: g.tier || "sub",
    legalName: g.legalname ?? undefined,
    kycState: g.kycstate ?? undefined,
    bankState: g.bankverificationstate ?? undefined,
  };
}

export function ApprovalQueueTab() {
  const seed = useSeed();
  // The real, API-backed project list (real numeric ids) when available,
  // falling back to the demo seed's projects otherwise - NOT seed.projects
  // directly. seed is always hydrated from the demo/fixture blob (GET
  // /api/workspace-seed - genuinely demo data regardless of mode), whose
  // project ids are fake slugs like "fs-04-entebbe", never real database
  // ids. The two real-mode fetches below used to map straight over
  // seed.projects, which meant every request they made was sent with an id
  // the real backend would always reject (it expects a number) - both
  // queues silently stayed empty in real mode no matter what real data
  // existed.
  const realProjects = useProjects();
  // Effect deps below key on this string, never the array object: both effects
  // setState, so an array-identity dep re-fires them on their own re-render —
  // an unbounded approver-queue / spend-request request loop that hangs the tab.
  const projectIdsKey = realProjects.map((project) => project.id).join(",");
  // Threshold asks (spec §6.3). Demo mode has no real Stratus login, so the DB read
  // returns nothing — the seed store is read instead, where demo-raised asks live
  // (mirrors the reporter inbox and contractor approval queue).
  //
  // Sourced from /spend-requests, NOT /approver-queue. The approver-queue route
  // gates on assertCanAct(..., "approve"), which requires approve_spend_ask /
  // approve_spend_ask_scoped — permissions stratus_admin deliberately does not
  // hold (approving an ask is the contractor lead's call). So it 403'd for every
  // oversight user on every project, allSettled dropped the rejections, and this
  // queue rendered its empty state as though nothing were waiting — hiding real
  // above-threshold asks from the very gate that exists to catch them.
  // /spend-requests gates on assertCanView instead, which oversight passes via
  // view_all; it's the same endpoint the confirmations effect below already uses.
  // Filtering therefore happens here rather than being implied by the endpoint.
  const demo = demoModeActive();
  const [dbThresholdAsks, setDbThresholdAsks] = useState<SpendRequest[]>([]);
  // Bumped after a threshold ask is approved/queried/rejected/verified from the
  // drawer, so the row actually leaves this queue instead of only updating
  // once something unrelated happens to remount the tab.
  const [thresholdTick, setThresholdTick] = useState(0);
  useEffect(() => {
    if (demo) return;
    let cancelled = false;
    Promise.allSettled(realProjects.map((project) => api.getSpendRequests(project.id))).then((results) => {
      if (cancelled) return;
      const rows = results
        .filter((result): result is PromiseFulfilledResult<Record<string, unknown>[]> => result.status === "fulfilled")
        .flatMap((result) => result.value)
        // Same three conditions as the demo branch below: still awaiting a
        // decision, over the gate, and not yet verified by Stratus. The amount
        // test goes through isAboveStratusThreshold rather than a literal —
        // this used to hardcode 100_000_000 against a 400M gate, routing every
        // ask in that band to oversight that the spec leaves with the lead.
        // Filtered on the raw row, BEFORE normalising: normalizeSpendRequestRow's
        // `threshold` option stamps thresholdFlag.routedTo = "stratus" on every
        // row it touches, and isAboveStratusThreshold short-circuits true on that
        // flag — so testing a normalised row would pass everything regardless of
        // amount.
        .filter(
          (row) =>
            (row.state === "proposed" || row.state === "flagged") &&
            isAboveStratusThreshold({ amount: Number(row.amount ?? 0) }) &&
            row.verifiedat == null,
        )
        .map((row) => normalizeSpendRequestRow(row, { threshold: true }));
      setDbThresholdAsks(rows);
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- projectIdsKey stands in for realProjects
  }, [demo, projectIdsKey, thresholdTick]);

  // The §6 confirm step: field evidence lands here for oversight to confirm
  // (separation of duties — the lead approved the spend, Stratus confirms it).
  // Demo mode keeps reading the seed store; real mode fetches each visible
  // project's real spend requests (same cross-project aggregation shape as
  // dbThresholdAsks just above) rather than reading demo-only seed data
  // regardless of mode - a real oversight user was previously shown (and
  // could try to act on) demo-seed rows with no backing DB record.
  const [dbConfirmations, setDbConfirmations] = useState<SpendRequest[]>([]);
  const [confirmationsTick, setConfirmationsTick] = useState(0);
  useEffect(() => {
    if (demo) return;
    let cancelled = false;
    Promise.allSettled(realProjects.map((project) => api.getSpendRequests(project.id))).then((results) => {
      if (cancelled) return;
      const rows = results
        .filter((result): result is PromiseFulfilledResult<Record<string, unknown>[]> => result.status === "fulfilled")
        .flatMap((result) => result.value)
        .filter((row) => row.state === "evidence_submitted")
        .map((row) => normalizeSpendRequestRow(row));
      setDbConfirmations(rows);
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- projectIdsKey stands in for realProjects
  }, [demo, projectIdsKey, confirmationsTick]);
  const confirmations = demo ? seed.spendRequests.filter((r) => r.state === "evidence_submitted") : dbConfirmations;

  const [sub, setSub] = useState("confirmations");

  // Stratus's first gate (spec §6.3): above-threshold asks awaiting Stratus verification.
  // Once Stratus marks one verified (verifiedAt set) it leaves this queue and moves to the
  // contractor lead's approval queue.
  const thresholdAsks = demo
    ? seed.spendRequests.filter(
        (r) => isAboveStratusThreshold(r) && (r.state === "proposed" || r.state === "flagged") && !r.verifiedAt,
      )
    : dbThresholdAsks;
  const toRoute = seed.disbursements.filter((d) => d.state === "queued_for_stratus");

  // Onboarding approval gate (§9.6). Demo reads the seed pipeline; API mode pulls
  // the live awaiting-approval orgs. `gatesTick` forces a refetch after approve/reject.
  const [dbGates, setDbGates] = useState<GateRow[]>([]);
  const [gatesTick, setGatesTick] = useState(0);
  useEffect(() => {
    if (demo) return;
    let cancelled = false;
    onboardingApi
      .listGates()
      .then(({ gates }) => { if (!cancelled) setDbGates(gates.map(gateRowFromApi)); })
      .catch(() => { if (!cancelled) setDbGates([]); });
    return () => { cancelled = true; };
  }, [demo, gatesTick]);
  const gates: GateRow[] = demo
    ? seed.organisations
        .filter((o) => o.pipelineStage === "awaiting_gate")
        .map((o) => ({ id: o.id, name: o.name, tier: o.tier }))
    : dbGates;

  // ZEUS-138 — pending project-creation requests (demo seed store or real API).
  const projectRequestsQuery = useProjectCreationRequests();
  const projectRequests = projectRequestsQuery.data;

  return (
    <div className="space-y-5">
      <Tabs
        variant="pill"
        items={[
          { value: "confirmations", label: "Evidence to confirm", count: confirmations.length },
          { value: "threshold", label: "Spend asks above threshold", count: thresholdAsks.length },
          { value: "disbursements", label: "Disbursements ready to route", count: toRoute.length },
          { value: "gates", label: "Onboarding Approval", count: gates.length },
          { value: "projectRequests", label: "Project requests", count: projectRequests.length },
        ]}
        value={sub}
        onValueChange={setSub}
      />
      <div>
        {sub === "confirmations" && <EvidenceConfirmations asks={confirmations} onActionComplete={() => setConfirmationsTick((n) => n + 1)} />}
        {sub === "threshold" && <ThresholdAsks asks={thresholdAsks} onActionComplete={() => setThresholdTick((n) => n + 1)} />}
        {sub === "disbursements" && <DisbursementsToRoute rows={toRoute} />}
        {sub === "gates" && <CascadeGates gates={gates} demo={demo} onChanged={() => setGatesTick((n) => n + 1)} />}
        {sub === "projectRequests" && <ProjectCreationRequestsTab requests={projectRequests} />}
      </div>
    </div>
  );
}

// Shared raw-DB-row → SpendRequest mapper for every real (non-demo) queue on
// this tab (threshold asks, evidence-to-confirm) - they read from the same
// underlying spend_request shape via different endpoints/filters.
// `thresholdFlag` is only meaningful for the threshold queue itself (it
// drives that queue's "why is this here" badge), so it's opt-in rather than
// always set.
export function normalizeSpendRequestRow(row: Record<string, unknown>, opts: { threshold?: boolean } = {}): SpendRequest {
  return {
    id: String(row.id),
    reporterId: String(row.requesterid ?? ""),
    organisationId: String(row.requesterorganisationid ?? row.organisationid ?? ""),
    projectId: String(row.projectid ?? ""),
    subProjectId: row.subprojectid == null ? undefined : String(row.subprojectid),
    milestoneId: row.milestoneid == null ? undefined : String(row.milestoneid),
    title: String(row.title ?? ""),
    amount: Number(row.amount ?? 0),
    currency: String(row.currency ?? "UGX") as SpendRequest["currency"],
    supplier: row.targetsupplier == null ? undefined : String(row.targetsupplier),
    purpose: String(row.purpose ?? ""),
    documents: [],
    state: String(row.state ?? "proposed") as SpendRequest["state"],
    submittedAt: String(row.createdat ?? new Date().toISOString()),
    // Independent-verification fields (migration 20260622120000). Dropping these
    // meant a verified ask came back with no verifiedAt, so it never left the
    // threshold queue and the Assurance panel could never read "verified" —
    // the Stratus-first gate had no way to close.
    ...(row.verifiedbyuserid == null ? {} : { verifiedById: String(row.verifiedbyuserid) }),
    ...(row.verifiedat == null ? {} : { verifiedAt: String(row.verifiedat) }),
    ...(row.verificationnote == null ? {} : { verificationNote: String(row.verificationnote) }),
    ...(opts.threshold ? { thresholdFlag: { rule: "database threshold", routedTo: "stratus" } as const } : {}),
  };
}

function EvidenceConfirmations({ asks, onActionComplete }: { asks: SpendRequest[]; onActionComplete?: () => void }) {
  const seed = useSeed();
  const money = useMoney();
  const people = usePeopleIndex();
  const orgs = useOrgIndex();
  const assuranceFor = useAssurance();
  const [open, setOpen] = useState<SpendRequest | null>(null);
  const evidenceCount = (id: string) => (seed.evidence ?? []).filter((e) => e.linkedAskId === id).length;
  // Reporters can record spend over the approved budget; flag those rows so oversight can
  // spot the overspend before opening the drawer (§6 spend flow / §10 oversight).
  const loggedFor = (id: string) => seed.spendItems.filter((i) => i.spendRequestId === id).reduce((s, i) => s + i.amount, 0);

  const columns: Column<SpendRequest>[] = [
    {
      key: "reporter",
      header: "Reporter",
      cell: (a) => (
        <div className="flex items-center gap-2">
          <Avatar initials={people[a.reporterId]?.avatarInitials ?? "?"} size={28} />
          <div>
            <p className="text-sm font-medium text-ink">{people[a.reporterId]?.fullName}</p>
            <p className="text-xs text-faint">{orgs[a.organisationId]?.name}</p>
          </div>
        </div>
      ),
    },
    {
      key: "project",
      header: "Project / Milestone",
      cell: (a) => (
        <div>
          <p className="text-sm text-ink">{seed.projects.find((p) => p.id === a.projectId)?.name}</p>
          <p className="text-xs text-faint">{seed.milestones.find((m) => m.id === a.milestoneId)?.name ?? "–"}</p>
        </div>
      ),
    },
    { key: "amount", header: "Amount", align: "right", cell: (a) => <span className="tabular-nums text-ink">{money(a.amount)}</span> },
    {
      key: "purpose",
      header: "Purpose",
      cell: (a) => (
        <div className="max-w-sm">
          <p className="line-clamp-2 text-sm text-mute">{a.title}</p>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <AssuranceBadge level={assuranceFor(a)} />
            {loggedFor(a.id) > a.amount && (
              <Pill variant="warn">Over budget · {money(loggedFor(a.id) - a.amount)}</Pill>
            )}
          </div>
        </div>
      ),
    },
    {
      key: "evidence",
      header: "Evidence",
      align: "center",
      cell: (a) => (
        <span className="inline-flex items-center gap-1.5 text-xs text-mute">
          <Camera className="h-3.5 w-3.5" /> <span className="tabular-nums">{evidenceCount(a.id)}</span>
        </span>
      ),
    },
    {
      key: "age",
      header: "Waiting",
      cell: (a) => {
        const hours = (Date.now() - new Date(a.submittedAt).getTime()) / 3.6e6;
        const tone = hours <= 48 ? "text-mute" : hours <= 168 ? "text-warn" : "text-bad";
        return <span className={`data ${tone}`}>{formatRelativeShort(a.submittedAt)}</span>;
      },
    },
    { key: "actions", header: "", align: "right", cell: (a) => <Button size="sm" variant="secondary" onClick={(e) => { e.stopPropagation(); setOpen(a); }}>Review evidence</Button> },
  ];

  return (
    <>
      <DataTable
        columns={columns}
        rows={asks}
        rowKey={(a) => a.id}
        onRowClick={setOpen}
        empty={<EmptyState title="No evidence awaiting confirmation" body="When a reporter submits evidence for an approved spend, it lands here for oversight to confirm." />}
      />
      <SpendAskDrawer ask={open} open={!!open} onOpenChange={(o) => !o && setOpen(null)} onActionComplete={onActionComplete} />
    </>
  );
}

function ThresholdAsks({ asks, onActionComplete }: { asks: SpendRequest[]; onActionComplete?: () => void }) {
  const money = useMoney();
  const people = usePeopleIndex();
  const orgs = useOrgIndex();
  const assuranceFor = useAssurance();
  const reviewFor = useAiReviewFor();
  const analystName = useAnalystName();
  const [open, setOpen] = useState<SpendRequest | null>(null);
  const columns: Column<SpendRequest>[] = [
    {
      key: "reporter",
      header: "Reporter",
      cell: (a) => (
        <div className="flex items-center gap-2">
          <Avatar initials={people[a.reporterId]?.avatarInitials ?? "?"} size={28} />
          <div>
            <p className="text-sm font-medium text-ink">{people[a.reporterId]?.fullName}</p>
            <p className="text-xs text-faint">{orgs[a.organisationId]?.name}</p>
          </div>
        </div>
      ),
    },
    { key: "amount", header: "Amount", align: "right", cell: (a) => <span className="tabular-nums text-ink">{money(a.amount)}</span> },
    {
      key: "purpose",
      header: "Purpose",
      cell: (a) => (
        <div className="max-w-sm">
          <p className="line-clamp-2 text-sm text-mute">{a.title}</p>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <AssuranceBadge level={assuranceFor(a)} />
            {/* TODO(heimdall): AI analyst not built yet — re-enable the per-row review badge when Heimdall ships.
            {(() => {
              const rev = reviewFor(a);
              return rev.state === "pending"
                ? <AnalystReviewingIndicator name={analystName} />
                : <HeimdallBadge state={rev.state} count={rev.findingIds.length} name={analystName} />;
            })()}
            */}
          </div>
        </div>
      ),
    },
    { key: "rule", header: "Rule", cell: () => <Pill variant="warn">Threshold · 1</Pill> },
    {
      key: "age",
      header: "In queue",
      cell: (a) => {
        const hours = (Date.now() - new Date(a.submittedAt).getTime()) / 3.6e6;
        const tone = hours <= 48 ? "text-mute" : hours <= 168 ? "text-warn" : "text-bad";
        return <span className={`data ${tone}`}>{formatRelativeShort(a.submittedAt)}</span>;
      },
    },
    { key: "actions", header: "", align: "right", cell: (a) => <Button size="sm" variant="secondary" onClick={(e) => { e.stopPropagation(); setOpen(a); }}>Review</Button> },
  ];
  return (
    <>
      <DataTable
        columns={columns}
        rows={asks}
        rowKey={(a) => a.id}
        onRowClick={setOpen}
        empty={<EmptyState title="No threshold-routed asks" body="Spend asks crossing the compliance threshold are routed here for Stratus review." />}
      />
      <SpendAskDrawer ask={open} open={!!open} onOpenChange={(o) => !o && setOpen(null)} onActionComplete={onActionComplete} />
    </>
  );
}

function DisbursementsToRoute({ rows }: { rows: Disbursement[] }) {
  const seed = useSeed();
  const money = useMoney();
  const [open, setOpen] = useState<Disbursement | null>(null);

  const ms = (id: string) => seed.milestones.find((m) => m.id === id);

  const columns: Column<Disbursement>[] = [
    {
      key: "milestone",
      header: "Milestone",
      cell: (d) => {
        const m = ms(d.milestoneId);
        const project = seed.projects.find((p) => p.id === m?.projectId);
        return (
          <div>
            <p className="font-medium text-ink">{m?.name}</p>
            <p className="text-xs text-faint">{project?.name}</p>
          </div>
        );
      },
    },
    { key: "amount", header: "Amount", align: "right", cell: (d) => <span className="tabular-nums text-ink">{money(d.amount)}</span> },
    { key: "rec", header: "Draft recommendation", cell: (d) => <span className="line-clamp-2 max-w-md text-sm text-mute">{d.stratusRecommendation}</span> },
    {
      key: "actions",
      header: "",
      align: "right",
      cell: (d) => (
        <div className="flex justify-end">
          <Button size="sm" onClick={() => setOpen(d)}>Review and route</Button>
        </div>
      ),
    },
  ];

  return (
    <>
      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(d) => d.id}
        onRowClick={setOpen}
        empty={<EmptyState title="Nothing ready to route" body="Milestones marked ready for disbursement by Stratus appear here, awaiting routing to the Danish Impact Fund." />}
      />
      <RouteDisbursementDrawer disbursement={open} onClose={() => setOpen(null)} />
    </>
  );
}

function RouteDisbursementDrawer({ disbursement, onClose }: { disbursement: Disbursement | null; onClose: () => void }) {
  const seed = useSeed();
  const money = useMoney();
  const { route: routeDisb, isPending: routing } = useDisbursementActions();
  const [comment, setComment] = useState("");
  const [confirmRoute, setConfirmRoute] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  if (!disbursement) return null;
  const d = disbursement;
  const milestone = seed.milestones.find((m) => m.id === d.milestoneId);
  const project = seed.projects.find((p) => p.id === milestone?.projectId);

  // The budget + evidence basis Stratus reviews before requesting the next tranche.
  const milestoneAsks = seed.spendRequests.filter(
    (r) => r.milestoneId === d.milestoneId && (r.state === "confirmed" || r.state === "evidence_submitted"),
  );
  const confirmed = milestoneAsks.filter((r) => r.state === "confirmed");
  const askIds = new Set(milestoneAsks.map((r) => r.id));
  const evidence = seed.evidence.filter((e) => askIds.has(e.linkedAskId));
  const projectEvidencePct = project && project.spent ? project.evidenced / project.spent : 0;

  async function route() {
    const note = comment.trim() || d.stratusRecommendation;
    await routeDisb(d.id, note);
    toast.success("Routed to the Danish Impact Fund with your recommendation");
    setComment("");
    onClose();
  }
  // Guard a typed recommendation from being lost on Escape / backdrop close.
  function requestClose(next: boolean) {
    if (next) return;
    if (comment.trim()) {
      setConfirmDiscard(true);
      return;
    }
    onClose();
  }
  function discardAndClose() {
    setComment("");
    onClose();
  }

  return (
    <Drawer open onOpenChange={requestClose} width="640px">
      <DrawerHeader>
        <DrawerTitle className="text-base font-semibold text-ink">{milestone?.name}</DrawerTitle>
        <p className="mt-0.5 text-xs text-faint">{project?.name} · {seed.programme.name}</p>
        <p className="mt-1 text-lg font-semibold tabular-nums text-ink">{money(d.amount)}</p>
      </DrawerHeader>

      <DrawerBody className="space-y-6">
        {milestone && <VerificationSummaryCard milestoneId={milestone.id} />}

        <section>
          <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-faint">Budget reviewed</h4>
          <div className="grid grid-cols-3 gap-2">
            <BudgetCell label="Tranche" value={money(d.amount)} />
            <BudgetCell label="Project spent" value={project ? money(project.spent) : "–"} />
            <BudgetCell label="Evidence coverage" value={formatPct(projectEvidencePct)} />
          </div>
        </section>

        <section>
          <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-faint">Spend on this milestone</h4>
          {milestoneAsks.length === 0 ? (
            <p className="text-sm text-faint">No itemised spend recorded against this milestone yet; the evidence pack is summarised below.</p>
          ) : (
            <ul className="divide-y divide-hairline rounded-md border border-line">
              {milestoneAsks.map((r) => (
                <li key={r.id} className="flex items-center justify-between gap-3 px-3 py-2">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-ink">{r.title}</p>
                    <p className="text-[11px] text-faint">{r.category ?? "Uncategorised"}{r.supplier ? ` · ${r.supplier}` : ""}</p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <Pill variant={r.state === "confirmed" ? "success" : "info"}>{r.state === "confirmed" ? "Confirmed" : "Evidence in"}</Pill>
                    <span className="whitespace-nowrap text-sm font-medium tabular-nums text-ink">{money(r.amount)}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
          <p className="mt-1.5 text-xs text-faint">{confirmed.length} confirmed · {milestoneAsks.length - confirmed.length} awaiting confirmation</p>
        </section>

        <section>
          <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-faint">Evidence reviewed</h4>
          {evidence.length === 0 ? (
            <p className="text-sm text-faint">Evidence is summarised in the draft recommendation below.</p>
          ) : (
            <ul className="grid grid-cols-2 gap-2">
              {evidence.slice(0, 4).map((e) => (
                <li key={e.id} className="rounded-md border border-line p-2">
                  <div className="h-20 overflow-hidden rounded bg-subtle">
                    {e.url ? <img src={e.url} alt="" className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center text-faint"><Camera className="h-5 w-5" /></div>}
                  </div>
                  <p className="mt-1.5 truncate text-xs font-medium text-ink">{e.filename ?? e.note ?? e.type}</p>
                  {e.geo && <p className="mt-0.5 flex items-center gap-1 text-[11px] text-faint"><MapPin className="h-3 w-3" /> {e.geo.lat.toFixed(3)}, {e.geo.lng.toFixed(3)}</p>}
                </li>
              ))}
            </ul>
          )}
        </section>

        <section>
          <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-faint">Your recommendation to the Danish Impact Fund</h4>
          <p className="mb-2 text-xs text-mute">This note is attached to the request and shown to the funder as the justification for releasing the tranche.</p>
          <Textarea
            autoFocus
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={d.stratusRecommendation ?? "Summarise your review of the evidence and budget, and your recommendation."}
            className="h-28"
          />
        </section>
      </DrawerBody>

      <DrawerFooter>
        <Button variant="ghost" onClick={() => requestClose(false)}>Cancel</Button>
        <Button disabled={routing} onClick={() => setConfirmRoute(true)}>Route to Danish Impact Fund</Button>
      </DrawerFooter>

      <AlertDialog
        open={confirmRoute}
        onOpenChange={setConfirmRoute}
        title="Route this tranche to the funder?"
        description={`This sends ${money(d.amount)} for ${milestone?.name} to the Danish Impact Fund with your recommendation attached. The fund decides whether to release it.`}
        confirmLabel={`Route ${money(d.amount)}`}
        onConfirm={route}
      />
      <AlertDialog
        open={confirmDiscard}
        onOpenChange={setConfirmDiscard}
        title="Discard your recommendation?"
        description="Your recommendation note has not been sent. If you close now it will be lost."
        confirmLabel="Discard"
        cancelLabel="Keep editing"
        tone="danger"
        onConfirm={discardAndClose}
      />
    </Drawer>
  );
}

function BudgetCell({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-line bg-subtle/40 px-3 py-2">
      <p className="text-[11px] text-faint">{label}</p>
      <p className="mt-0.5 text-sm font-semibold tabular-nums text-ink">{value}</p>
    </div>
  );
}

function CascadeGates({ gates, demo, onChanged }: { gates: GateRow[]; demo: boolean; onChanged: () => void }) {
  const mutate = useMutate();
  const [confirmGate, setConfirmGate] = useState<GateRow | null>(null);
  const [rejectTarget, setRejectTarget] = useState<GateRow | null>(null);
  const [detailGate, setDetailGate] = useState<GateRow | null>(null);
  const [busy, setBusy] = useState(false);

  // Demo mode has no real oversight session, so approve/reject mutate the seed
  // org's pipeline stage directly; API mode calls the gate endpoints and refetches.
  async function approve(g: GateRow) {
    if (demo) {
      mutate((s) => ({
        ...s,
        organisations: s.organisations.map((o) => (o.id === g.id ? { ...o, pipelineStage: "active" } : o)),
      }));
      toast.success(`Gate approved for ${g.name}`);
      return;
    }
    setBusy(true);
    try {
      const res = await onboardingApi.approveGate(g.id);
      toast.success(
        res.emailSent
          ? `Gate approved — ${g.name} notified by email`
          : `Gate approved for ${g.name}`,
      );
      onChanged();
    } catch {
      toast.error(`Couldn't approve ${g.name} — please retry`);
    } finally {
      setBusy(false);
    }
  }

  // Reject (spec §9.6 approve/reject/comment): turns the contractor down. The org is
  // suspended and the contractor is told at login their onboarding wasn't approved;
  // the reason is recorded for the audit trail. NOTE: the third §9.6 affordance —
  // a "comment" / send-back-for-more-info delivered to the contractor — is deferred
  // until there's a notification channel, so only approve + reject are surfaced.
  async function reject(g: GateRow, reason: string) {
    if (demo) {
      mutate((s) => ({
        ...s,
        organisations: s.organisations.map((o) => (o.id === g.id ? { ...o, pipelineStage: "suspended" } : o)),
      }));
      toast.message(`Onboarding rejected for ${g.name}`);
      return;
    }
    const res = await onboardingApi.rejectGate(g.id, reason);
    toast.message(
      res.emailSent
        ? `Onboarding rejected — ${g.name} notified by email`
        : `Onboarding rejected for ${g.name} (email not sent)`,
    );
    onChanged();
  }

  const columns: Column<GateRow>[] = [
    { key: "org", header: "Organisation", cell: (o) => <span className="font-medium text-ink">{o.name}</span> },
    { key: "tier", header: "Tier", cell: (o) => <Pill variant={o.tier === "prime" ? "brand" : "neutral"}>{o.tier === "prime" ? "Prime" : "Sub"}</Pill> },
    { key: "rule", header: "Triggering rule", cell: () => <span className="text-sm text-mute">Onboarding submitted, requires Stratus sign-off</span> },
    {
      key: "actions",
      header: "",
      align: "right",
      cell: (o) => (
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="ghost" disabled={busy} onClick={(e) => { e.stopPropagation(); setRejectTarget(o); }}>Reject</Button>
          <Button size="sm" variant="secondary" onClick={(e) => { e.stopPropagation(); setDetailGate(o); }}>View details</Button>
          <Button size="sm" disabled={busy} onClick={(e) => { e.stopPropagation(); setConfirmGate(o); }}>Approve gate</Button>
        </div>
      ),
    },
  ];
  return (
    <>
      <DataTable
        columns={columns}
        rows={gates}
        rowKey={(o) => o.id}
        onRowClick={setDetailGate}
        empty={<EmptyState title="No gates awaiting sign-off" body="Contractors who have submitted onboarding and need Stratus approval appear here." />}
      />
      <OnboardingDetailDrawer
        gate={detailGate}
        open={detailGate != null}
        onOpenChange={(o) => !o && setDetailGate(null)}
        onApprove={(g) => { setDetailGate(null); setConfirmGate(g); }}
        onReject={(g) => { setDetailGate(null); setRejectTarget(g); }}
      />
      <AlertDialog
        open={confirmGate != null}
        onOpenChange={(o) => !o && setConfirmGate(null)}
        title="Approve this onboarding gate?"
        description={confirmGate ? `This admits ${confirmGate.name} onto the platform and lets work and spend flow to them. Sign off only once their checks are satisfied.` : undefined}
        confirmLabel="Approve gate"
        onConfirm={() => { const g = confirmGate; if (g) approve(g); }}
      />
      <RejectGateDrawer gate={rejectTarget} onReject={reject} onClose={() => setRejectTarget(null)} />
    </>
  );
}

// Reject an onboarding gate with a required reason. The reason is recorded for the
// audit trail; the contractor is told (generically) at login that their onboarding
// wasn't approved. Honest copy: it does NOT claim a message is sent to them.
function RejectGateDrawer({ gate, onReject, onClose }: { gate: GateRow | null; onReject: (g: GateRow, reason: string) => Promise<void>; onClose: () => void }) {
  const [note, setNote] = useState("");
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!gate) return null;

  async function send() {
    if (!note.trim()) return;
    setBusy(true);
    try {
      await onReject(gate!, note.trim());
      setNote("");
      onClose();
    } catch {
      toast.error(`Couldn't reject ${gate!.name} — please retry`);
    } finally {
      setBusy(false);
    }
  }
  function requestClose(next: boolean) {
    if (next) return;
    if (note.trim()) {
      setConfirmDiscard(true);
      return;
    }
    onClose();
  }

  return (
    <Drawer open onOpenChange={requestClose} width="520px">
      <DrawerHeader>
        <DrawerTitle className="text-base font-semibold text-ink">Reject {gate.name}</DrawerTitle>
        <p className="mt-1 text-xs text-faint">This turns the contractor down — their organisation is suspended, they're emailed the reason, and it's recorded in the audit trail.</p>
      </DrawerHeader>
      <DrawerBody>
        <Textarea
          autoFocus
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Reason for rejection — emailed to the contractor and recorded (required)"
          className="h-28"
        />
      </DrawerBody>
      <DrawerFooter>
        <Button variant="ghost" onClick={() => requestClose(false)}>Cancel</Button>
        <Button onClick={send} disabled={!note.trim() || busy}>Reject onboarding</Button>
      </DrawerFooter>

      <AlertDialog
        open={confirmDiscard}
        onOpenChange={setConfirmDiscard}
        title="Discard this rejection?"
        description="Your reason has not been submitted. If you close now it will be lost."
        confirmLabel="Discard"
        cancelLabel="Keep editing"
        tone="danger"
        onConfirm={() => { setNote(""); onClose(); }}
      />
    </Drawer>
  );
}

// ZEUS-138 — contractor/subcontractor project-creation requests awaiting
// stratus_admin review. Works in both demo mode (backed by the seed store's
// projectCreationRequests) and real-API mode — useProjectCreationRequests
// normalizes both into the same row shape.
function ProjectCreationRequestsTab({ requests }: { requests: ProjectCreationRequestRow[] }) {
  const [reviewTarget, setReviewTarget] = useState<ProjectCreationRequestRow | null>(null);
  const [rejectTarget, setRejectTarget] = useState<ProjectCreationRequestRow | null>(null);

  const columns: Column<ProjectCreationRequestRow>[] = [
    {
      key: "proposal",
      header: "Proposed project",
      cell: (r) => (
        <div className="max-w-sm">
          <p className="text-sm font-medium text-ink">{r.proposedName}</p>
          <p className="line-clamp-2 text-xs text-mute">{r.proposedDescription}</p>
        </div>
      ),
    },
    {
      key: "requester",
      header: "Requested by",
      cell: (r) => <span className="text-sm capitalize text-mute">{r.requesterRole.replace(/_/g, " ")}</span>,
    },
    {
      key: "justification",
      header: "Justification",
      cell: (r) => <p className="line-clamp-2 max-w-xs text-sm text-mute">{r.justification}</p>,
    },
    {
      key: "age",
      header: "Submitted",
      cell: (r) => <span className="text-sm text-mute">{formatRelativeShort(r.createdAt)}</span>,
    },
    {
      key: "actions",
      header: "",
      align: "right",
      cell: (r) => (
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); setRejectTarget(r); }}>Reject</Button>
          <Button size="sm" onClick={(e) => { e.stopPropagation(); setReviewTarget(r); }}>Review</Button>
        </div>
      ),
    },
  ];

  return (
    <>
      <DataTable
        columns={columns}
        rows={requests}
        rowKey={(r) => String(r.id)}
        onRowClick={setReviewTarget}
        empty={<EmptyState title="No pending project requests" body="When a contractor or subcontractor requests a new project, it lands here for Stratus review." />}
      />
      {reviewTarget && (
        <CreateProjectModal
          open
          onOpenChange={(o) => !o && setReviewTarget(null)}
          requestId={String(reviewTarget.id)}
          programmeId={reviewTarget.programmeId}
          presetParentProjectId={reviewTarget.parentProjectId}
          initialValues={{
            name: reviewTarget.proposedName,
            description: reviewTarget.proposedDescription,
            parentProjectId: reviewTarget.parentProjectId,
          }}
        />
      )}
      <RejectProjectRequestDrawer request={rejectTarget} onClose={() => setRejectTarget(null)} />
    </>
  );
}

// Reject a pending project-creation request with a required reason (spec:
// "Must provide written reason for rejection") — mirrors RejectGateDrawer.
function RejectProjectRequestDrawer({ request, onClose }: { request: ProjectCreationRequestRow | null; onClose: () => void }) {
  const { rejectProjectCreationRequest, isPending } = useRejectProjectCreationRequest();
  const [reason, setReason] = useState("");
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  if (!request) return null;

  async function send() {
    if (!reason.trim() || !request) return;
    const ok = await rejectProjectCreationRequest(request.id, reason.trim());
    if (ok) {
      toast.message(`Rejected "${request.proposedName}"`);
      setReason("");
      onClose();
    } else {
      toast.error("Couldn't reject the request — please retry");
    }
  }
  function requestClose(next: boolean) {
    if (next) return;
    if (reason.trim()) {
      setConfirmDiscard(true);
      return;
    }
    onClose();
  }

  return (
    <Drawer open onOpenChange={requestClose} width="520px">
      <DrawerHeader>
        <DrawerTitle className="text-base font-semibold text-ink">Reject "{request.proposedName}"</DrawerTitle>
        <p className="mt-1 text-xs text-faint">The requester is notified with your reason, and it's recorded in the audit trail.</p>
      </DrawerHeader>
      <DrawerBody>
        <Textarea
          autoFocus
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Reason for rejection — sent to the requester and recorded (required)"
          className="h-28"
        />
      </DrawerBody>
      <DrawerFooter>
        <Button variant="ghost" onClick={() => requestClose(false)}>Cancel</Button>
        <Button onClick={send} disabled={!reason.trim() || isPending}>Reject request</Button>
      </DrawerFooter>

      <AlertDialog
        open={confirmDiscard}
        onOpenChange={setConfirmDiscard}
        title="Discard this rejection?"
        description="Your reason has not been submitted. If you close now it will be lost."
        confirmLabel="Discard"
        cancelLabel="Keep editing"
        tone="danger"
        onConfirm={() => { setReason(""); onClose(); }}
      />
    </Drawer>
  );
}

